import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { LogOut, ShieldCheck, User, Menu, X, Globe } from "lucide-react";
import { WELWORTH_LOGO } from "@/lib/brand";
import { SUPPORTED_LANGUAGES } from "@/i18n";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const loc = useLocation();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const isActive = (p) => loc.pathname === p;
  const closeMenu = () => { setMenuOpen(false); setLangOpen(false); };

  const links = [
    { to: "/", label: t("nav.home"),  testid: "nav-home" },
    { to: "/pricing", label: t("nav.pricing"), testid: "nav-pricing" },
    { to: "/about", label: t("nav.about"), testid: "nav-about" },
    { to: "/book", label: t("nav.book"), testid: "nav-book" },
    { to: "/dashboard", label: "Track Booking", testid: "nav-track" },
  ];

  const currentLangLabel = (i18n.language || "en").startsWith("mr") ? "मराठी" : "English";

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem("welworth.lang", code);
      localStorage.setItem("welworth.lang.chosen", "1");
    } catch (err) { console.warn("[Navbar] localStorage write failed:", err); }
    setLangOpen(false);
  };

  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-black/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 sm:gap-3 min-w-0" data-testid="navbar-logo" onClick={closeMenu}>
          <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center shrink-0" style={{ mixBlendMode: "multiply" }}>
            <img src={WELWORTH_LOGO} alt="Welworth Creative Media" className="max-h-full max-w-full object-contain" style={{ mixBlendMode: "multiply" }} />
          </div>
          <div className="leading-tight min-w-0">
            <div className="font-display text-lg sm:text-xl font-bold text-black truncate">{t("meta.brand")}</div>
            <div className="text-[9px] sm:text-[10px] tracking-overline uppercase text-gray-500 font-semibold truncate">{t("meta.brand_tagline")}</div>
          </div>
        </Link>

        {/* desktop nav */}
        <nav className="hidden md:flex items-center gap-8 text-sm">
          {links.map((l) => (
            <Link key={l.to} to={l.to}
              className={`transition border-b ${isActive(l.to) ? "text-black border-black" : "text-gray-600 border-transparent hover:text-black"}`}
              data-testid={l.testid}>
              {l.label}
            </Link>
          ))}
          {user && user.role === "admin" && (
            <Link to="/admin" className="text-gray-600 hover:text-black flex items-center gap-1" data-testid="nav-admin">
              <ShieldCheck size={14}/> {t("nav.admin")}
            </Link>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {/* Language toggle */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="text-sm px-3 py-2 border border-black/15 rounded-sm hover:bg-black hover:text-white hover:border-black flex items-center gap-1.5 transition"
              data-testid="nav-lang-toggle"
              aria-label={t("nav.language")}
            >
              <Globe size={14}/> {currentLangLabel}
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white border border-black/15 rounded-sm shadow-lg py-1" data-testid="nav-lang-menu">
                {SUPPORTED_LANGUAGES.map((L) => (
                  <button
                    key={L.code}
                    onClick={() => changeLang(L.code)}
                    className={"w-full text-left px-4 py-2 text-sm hover:bg-gray-100 " + (i18n.language === L.code ? "font-bold text-black" : "text-gray-700")}
                    data-testid={`nav-lang-${L.code}`}
                  >
                    {L.native} <span className="text-xs text-gray-400 ml-1">{L.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {user && user.email && user.role === "admin" ? (
            <>
              <Link to="/admin" className="text-gray-600 hover:text-black flex items-center gap-1 text-sm" data-testid="nav-admin-corner">
                <ShieldCheck size={14}/> {t("nav.admin")}
              </Link>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="text-sm px-3 py-2 border border-black/15 hover:bg-black hover:text-white hover:border-black rounded-sm flex items-center gap-2 transition"
                data-testid="nav-logout"
              ><LogOut size={14}/> {t("nav.logout")}</button>
            </>
          ) : (
            <Link to="/book" className="text-sm px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-sm transition" data-testid="nav-register">
              {t("home.cta_band_button")}
            </Link>
          )}
        </div>

        {/* mobile menu trigger */}
        <button
          className="md:hidden p-2 -mr-2 text-black"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {menuOpen ? <X size={22}/> : <Menu size={22}/>}
        </button>
      </div>

      {/* mobile drawer */}
      {menuOpen && (
        <div className="md:hidden border-t border-black/10 bg-white" data-testid="nav-mobile-drawer">
          <div className="px-5 py-4 flex flex-col gap-3 text-sm">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={closeMenu}
                className={`py-1.5 ${isActive(l.to) ? "text-black font-semibold" : "text-gray-700"}`}
                data-testid={`mob-${l.testid}`}>
                {l.label}
              </Link>
            ))}
            {user && user.role === "admin" && (
              <Link to="/admin" onClick={closeMenu} className="py-1.5 text-gray-700 flex items-center gap-1" data-testid="mob-nav-admin">
                <ShieldCheck size={14}/> {t("nav.admin")}
              </Link>
            )}
            {/* Mobile language switch */}
            <div className="pt-3 border-t border-black/10">
              <div className="text-[10px] tracking-overline uppercase text-gray-500 font-semibold mb-1.5">{t("nav.language")}</div>
              <div className="flex gap-2">
                {SUPPORTED_LANGUAGES.map((L) => (
                  <button
                    key={L.code}
                    onClick={() => changeLang(L.code)}
                    className={"px-3 py-1.5 border rounded-sm text-sm " + (i18n.language === L.code ? "border-black bg-black text-white" : "border-black/20 text-gray-700")}
                    data-testid={`mob-nav-lang-${L.code}`}
                  >{L.native}</button>
                ))}
              </div>
            </div>
            <div className="pt-3 border-t border-black/10 flex flex-col gap-2">
              {user && user.email && user.role === "admin" ? (
                <>
                  <Link to="/admin" onClick={closeMenu} className="py-1.5 text-gray-700 flex items-center gap-2" data-testid="mob-nav-admin-corner">
                    <ShieldCheck size={14}/> {t("nav.admin")}
                  </Link>
                  <button onClick={async () => { await logout(); closeMenu(); nav("/"); }}
                    className="px-3 py-2 border border-black/15 rounded-sm flex items-center justify-center gap-2 text-sm hover:bg-black hover:text-white transition"
                    data-testid="mob-nav-logout"
                  ><LogOut size={14}/> {t("nav.logout")}</button>
                </>
              ) : (
                <Link to="/book" onClick={closeMenu}
                  className="px-3 py-2 bg-black text-white text-center rounded-sm text-sm hover:bg-gray-800 transition"
                  data-testid="mob-nav-register">{t("home.cta_band_button")}</Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
